import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { adminLogin, adminRegister } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Helper function for button label
  const getSubmitButtonLabel = () => {
    return isRegister ? 'Crear cuenta' : 'Iniciar sesión';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password || (isRegister && !name)) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    try {
      setLoading(true);
      if (isRegister) {
        await adminRegister(name, email, password);
        toast.success('¡Cuenta de administrador creada!');
      } else {
        await adminLogin(email, password);
        toast.success('¡Bienvenido administrador!');
      }
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || error.message || 'Error de autenticación';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1C1917] flex items-center justify-center" data-testid="admin-login-page">
      <div className="w-full max-w-md px-6 py-16">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#BA7B7C] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 
            className="text-3xl font-light tracking-tight text-white mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Panel de Administración
          </h1>
          <p className="text-[#A8A29E]">
            {isRegister ? 'Crear cuenta de administrador' : 'Iniciar sesión como administrador'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
          {isRegister && (
            <div>
              <label className="block text-sm text-[#A8A29E] mb-2">
                Nombre
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white focus:outline-none focus:border-[#BA7B7C] transition-colors placeholder-white/50"
                placeholder="Tu nombre"
                data-testid="admin-name-input"
              />
            </div>
          )}

          <div>
            <label className="block text-sm text-[#A8A29E] mb-2">
              Correo electrónico
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white focus:outline-none focus:border-[#BA7B7C] transition-colors placeholder-white/50"
              placeholder="admin@email.com"
              data-testid="admin-email-input"
            />
          </div>

          <div>
            <label className="block text-sm text-[#A8A29E] mb-2">
              Contraseña
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white focus:outline-none focus:border-[#BA7B7C] transition-colors pr-12 placeholder-white/50"
                placeholder="••••••••"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#BA7B7C] text-white rounded-full py-4 hover:bg-[#A36566] transition-colors"
            data-testid="admin-login-submit"
          >
            {loading ? 'Procesando...' : getSubmitButtonLabel()}
          </button>
        </form>

        <p className="text-center text-[#A8A29E] mt-8">
          {isRegister ? '¿Ya tienes cuenta de admin?' : '¿Primera vez?'}{' '}
          <button 
            onClick={() => setIsRegister(!isRegister)}
            className="text-[#BA7B7C] hover:text-white transition-colors"
          >
            {isRegister ? 'Iniciar sesión' : 'Crear cuenta'}
          </button>
        </p>

        <div className="mt-8 pt-8 border-t border-white/10">
          <Link 
            to="/" 
            className="block text-center text-sm text-[#A8A29E] hover:text-white transition-colors"
          >
            ← Volver a la tienda
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
